export type FoodId =
  | 'glucose'
  | 'sucrose'
  | 'starch-solution'
  | 'egg-white'
  | 'milk'
  | 'vegetable-oil'
  | 'orange-juice'
  | 'water'
  | 'potato'
  | 'bread';

export type FoodComponent =
  | 'reducingSugar'
  | 'nonReducingSugar'
  | 'starch'
  | 'protein'
  | 'lipid'
  | 'vitaminC';

export type FoodForm = 'solution' | 'suspension' | 'liquid-mixture' | 'oil' | 'solid';

export interface FoodComposition {
  /** Relative concentration from 0 (not detected) to 1 (strong reference sample). */
  reducingSugar: number;
  nonReducingSugar: number;
  starch: number;
  protein: number;
  lipid: number;
  vitaminC: number;
}

export interface FoodSample {
  id: FoodId;
  name: string;
  form: FoodForm;
  composition: FoodComposition;
  preparation: string;
  notes: string;
}

const absentComponents: FoodComposition = {
  reducingSugar: 0,
  nonReducingSugar: 0,
  starch: 0,
  protein: 0,
  lipid: 0,
  vitaminC: 0,
};

const food = (
  id: FoodId,
  name: string,
  form: FoodForm,
  composition: Partial<FoodComposition>,
  preparation: string,
  notes: string,
): FoodSample => ({
  id,
  name,
  form,
  composition: { ...absentComponents, ...composition },
  preparation,
  notes,
});

/**
 * Educational relative profiles for standard school food tests, not nutritional labels.
 * Values are normalized from 0 to 1 so test effects can be compared consistently.
 */
export const foodDatabase: Record<FoodId, FoodSample> = {
  glucose: food(
    'glucose',
    'Glucose solution',
    'solution',
    { reducingSugar: 0.95 },
    'Use the supplied solution directly.',
    'Reducing-sugar positive control.',
  ),
  sucrose: food(
    'sucrose',
    'Sucrose solution',
    'solution',
    { nonReducingSugar: 0.95 },
    'Use the supplied solution directly.',
    'Negative with Benedict’s reagent until hydrolysed and neutralised.',
  ),
  'starch-solution': food(
    'starch-solution',
    'Starch solution',
    'suspension',
    { starch: 0.95 },
    'Shake gently before taking a sample.',
    'Starch positive control.',
  ),
  'egg-white': food(
    'egg-white',
    'Egg white (albumin)',
    'liquid-mixture',
    { protein: 0.9, lipid: 0.01 },
    'Use a small portion of the prepared albumin solution.',
    'Protein-rich sample; use care with alkaline Biuret reagents.',
  ),
  milk: food(
    'milk',
    'Milk',
    'liquid-mixture',
    { reducingSugar: 0.12, protein: 0.38, lipid: 0.35 },
    'Mix gently before sampling.',
    'Contains reducing lactose, protein, and lipid; results are relative to this prepared sample.',
  ),
  'vegetable-oil': food(
    'vegetable-oil',
    'Vegetable oil',
    'oil',
    { lipid: 0.98 },
    'Use a small measured volume.',
    'Lipid positive control.',
  ),
  'orange-juice': food(
    'orange-juice',
    'Orange juice',
    'liquid-mixture',
    { reducingSugar: 0.62, vitaminC: 0.72 },
    'Mix gently; filter pulp for drop-count comparisons.',
    'Contains reducing sugars and vitamin C; vitamin C varies with freshness and heating.',
  ),
  water: food(
    'water',
    'Water',
    'solution',
    {},
    'Use as supplied.',
    'Negative control.',
  ),
  potato: food(
    'potato',
    'Potato',
    'solid',
    { reducingSugar: 0.04, starch: 0.72, protein: 0.08, lipid: 0.02, vitaminC: 0.12 },
    'Grind a small piece with distilled water before liquid tests; use a fresh cut surface for iodine.',
    'Starch-rich solid; grind before sampling for solution-based tests.',
  ),
  bread: food(
    'bread',
    'Bread',
    'solid',
    { reducingSugar: 0.08, starch: 0.78, protein: 0.12, lipid: 0.04 },
    'Grind a small piece with distilled water before liquid tests; use a crumb on a white tile for iodine.',
    'Starch-rich solid; composition varies by bread type.',
  ),
};

export function getFood(foodId: string): FoodSample {
  if (!Object.prototype.hasOwnProperty.call(foodDatabase, foodId)) {
    throw new RangeError(`Unknown food sample: ${foodId}`);
  }
  return foodDatabase[foodId as FoodId];
}

export function listFoodIds(): FoodId[] {
  return Object.keys(foodDatabase) as FoodId[];
}

export function listFoods(): FoodSample[] {
  return listFoodIds().map((foodId) => foodDatabase[foodId]);
}